
import mcpConfig from "mcp/config/mcp_config.json";

export interface MCPServerConfig {
    name: string;
    description?: string;
    url: string;
    env?: Record<string, string>;
}

export interface UnifiedMCPConfig {
    servers: MCPServerConfig[];
}

export class MCPConfigService {
    private static instance: MCPConfigService;
    private config: UnifiedMCPConfig;

    private constructor() {
        this.loadConfigs();
    }

    public static getInstance(): MCPConfigService {
        if (!MCPConfigService.instance) {
            MCPConfigService.instance = new MCPConfigService();
        }
        return MCPConfigService.instance;
    }

    /**
     * Substitutes environment variable placeholders in a string.
     * Supports ${VAR_NAME} syntax.
     * Falls back to the original placeholder if the environment variable is not defined.
     *
     * Browser environment only - uses import.meta.env (Vite)
     */
    private substituteEnvVars(value: string): string {
        return value.replace(/\$\{([^}]+)}/g, (match, varName) => {
            const envValue = import.meta.env[varName];

            if (envValue !== undefined) {
                return envValue;
            }

            console.warn(`Environment variable ${varName} is not defined, using placeholder: ${match}`);
            return match;
        });
    }

    /**
     * Recursively processes an object to substitute environment variables in all string values.
     */
    private processConfigObject<T>(obj: T): T {
        if (typeof obj === "string") {
            return this.substituteEnvVars(obj) as T;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.processConfigObject(item)) as T;
        }

        if (obj !== null && typeof obj === "object") {
            const processed: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(obj)) {
                processed[key] = this.processConfigObject(value);
            }
            return processed as T;
        }

        return obj;
    }

    private loadConfigs(): void {
        try {
            // Load the raw config and process it to substitute environment variables
            const rawConfig = mcpConfig as UnifiedMCPConfig;
            this.config = this.processConfigObject(rawConfig);
            // console.log("MCP Config loaded with environment variable substitution:", this.config);
        }
        catch (error) {
            console.error("Failed to load MCP configurations:", error);
            this.config = {
                servers: []
            };
        }
    }

    // Server configuration methods
    public getServerConfigs(): MCPServerConfig[] {
        return this.config.servers;
    }

    public getServerConfigByName(name: string): MCPServerConfig | undefined {
        return this.config.servers.find(config => config.name === name);
    }

    public addServerConfig(config: MCPServerConfig): void {
        this.config.servers.push(config);
    }

    public removeServerConfig(name: string): void {
        this.config.servers = this.config.servers.filter(config => config.name !== name);
    }

    public updateServerConfig(name: string, updatedConfig: MCPServerConfig): void {
        const index = this.config.servers.findIndex(config => config.name === name);
        if (index !== -1) {
            this.config.servers[index] = updatedConfig;
        }
    }

    // Validation methods
    private isValidUrl(url: string): boolean {
        try {
            new URL(url);
            return true;
        }
        catch {
            return false;
        }
    }

    public validateServerConfig(config: MCPServerConfig): string[] {
        const errors: string[] = [];

        if (!config.name?.trim()) {
            errors.push("Server name is required");
        }

        if (!config.url?.trim()) {
            errors.push("Server URL is required");
        }
        else if (config.url.includes("${")) {
            errors.push("Server URL contains unresolved environment variable placeholders");
        }
        else if (!this.isValidUrl(config.url)) {
            errors.push("Server URL must be a valid URL");
        }

        return errors;
    }

    // Legacy method for backward compatibility
    public getConfigs(): MCPServerConfig[] {
        return this.getServerConfigs();
    }

    public validateConfig(config: MCPServerConfig): string[] {
        return this.validateServerConfig(config);
    }
}

export const mcpConfigService = MCPConfigService.getInstance();